import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Button, Divider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useCart } from "../contexts/cart-context";

interface CartItem {
  id: number;
  name: string;
  slug?: string;
  category: string;
  price: number;
  quantity: number;
  image: string;
}

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export const CartModal: React.FC<CartModalProps> = ({ isOpen, onClose, onCheckout }) => {
  const { items, removeItem, updateQuantity, totalPrice } = useCart();

  const [highlightTotal, setHighlightTotal] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;
    setHighlightTotal(true);
    const timeout = setTimeout(() => setHighlightTotal(false), 250);
    return () => clearTimeout(timeout);
  }, [totalPrice]);

  const formatItemsCount = (count: number) => {
    if (count === 1) return '1 товар';
    if (count > 1 && count < 5) return `${count} товара`;
    return `${count} товаров`;
  };

  const handleQuantityChange = (id: number, change: number) => {
    const item = items.find(item => item.id === id);
    if (item) {
      updateQuantity(id, Math.max(1, item.quantity + change));
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size="md"
      backdrop="blur"
      classNames={{
        base: "m-0 h-screen max-h-screen",
        wrapper: "!items-stretch",
      }}
    >
      <DrawerContent className="h-screen max-h-screen flex flex-col rounded-none w-full sm:w-[420px]">
        {(close) => (
          <>
            <DrawerHeader className="flex items-center justify-between px-4 py-3 border-b border-divider">
              <div className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">
                  Корзина
                  {items.length > 0 && (
                    <span className="ml-1 text-sm font-normal text-foreground/70">
                      · {formatItemsCount(items.length)}
                    </span>
                  )}
                </h3>
                {items.length > 0 && (
                  <p className="text-xs text-foreground/60">
                    Вы можете изменить количество или удалить товары перед оформлением заказа.
                  </p>
                )}
              </div>
            </DrawerHeader>
            <Divider />
            <DrawerBody className="flex-1 overflow-y-auto px-4">
              {items.length > 0 ? (
                <>
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-4 py-4">
                      <div className="w-20 h-20 bg-content2 flex-shrink-0">
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between">
                          <div className="pr-2">
                            <h4 className="font-medium leading-snug">
                              {item.slug ? (
                                <RouterLink
                                  to={`/product-detail/${item.slug}`}
                                  className="hover:underline"
                                  onClick={() => close()}
                                >
                                  {item.name}
                                </RouterLink>
                              ) : (
                                <span>{item.name}</span>
                              )}
                            </h4>
                            <p className="text-sm text-foreground/70">{item.category}</p>
                          </div>
                          <Button 
                            isIconOnly 
                            size="sm" 
                            variant="light" 
                            onPress={() => removeItem(item.id)}
                            aria-label="Удалить из корзины"
                          >
                            <Icon icon="lucide:trash-2" className="text-danger" width={16} />
                          </Button>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <div className="flex items-center border border-divider">
                            <Button 
                              isIconOnly 
                              size="sm" 
                              variant="flat" 
                              onPress={() => handleQuantityChange(item.id, -1)}
                              className="rounded-none"
                            >
                              <Icon icon="lucide:minus" width={16} />
                            </Button>
                            <span className="px-4">{item.quantity}</span>
                            <Button 
                              isIconOnly 
                              size="sm" 
                              variant="flat" 
                              onPress={() => handleQuantityChange(item.id, 1)}
                              className="rounded-none"
                            >
                              <Icon icon="lucide:plus" width={16} />
                            </Button>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-foreground/60">
                              {item.quantity} × {item.price} ₽
                            </p>
                            <p className="font-medium">{item.price * item.quantity} ₽</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Divider className="my-2" />
                </>
              ) : (
                <div className="py-12 text-center">
                  <Icon icon="lucide:shopping-bag" className="mx-auto mb-4 text-foreground/40" width={48} />
                  <h4 className="text-lg font-medium mb-2">Ваша корзина пуста</h4>
                  <p className="text-foreground/70">Добавьте товары из каталога</p>
                  <Button
                    color="primary"
                    variant="flat"
                    className="mt-6"
                    onPress={close}
                    as={RouterLink}
                    to="/catalog"
                  >
                    Перейти в каталог
                  </Button>
                </div>
              )}
            </DrawerBody>
            {items.length > 0 && (
              <DrawerFooter className="border-t border-divider px-4 py-4 flex flex-col gap-3">
                <div
                  className={`flex justify-between w-full items-baseline rounded-md px-2 py-1 transition-colors duration-200 ${
                    highlightTotal ? "bg-content2/60" : "bg-transparent"
                  }`}
                >
                  <span className="text-sm text-foreground/70">Итого</span>
                  <span className="text-lg font-semibold">{totalPrice} ₽</span>
                </div>
                <div className="flex flex-col gap-1 text-xs text-foreground/60">
                  <div className="flex items-center gap-1.5">
                    <Icon icon="lucide:truck" width={14} className="text-foreground/70" />
                    <span>Стоимость доставки будет рассчитана при оформлении заказа.</span>
                  </div>
                </div>
                <div className="flex gap-2 w-full">
                  <Button
                    fullWidth
                    variant="flat"
                    onPress={close}
                  >
                    Продолжить покупки
                  </Button>
                  <Button
                    fullWidth
                    color="primary"
                    onPress={onCheckout}
                  >
                    Оформить заказ
                  </Button>
                </div>
              </DrawerFooter>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};